<script setup lang="ts">
// Map Review bottom drawer: one table at a time over the map, respecting the
// shared filter spec. The drawer and the map both read and write the same Pinia
// store, so a filter set anywhere is in force everywhere.
//
// Height is the store's `drawerFraction` — shared pointer-drag state (default
// ~1/3 of the map, dragged up to cover it) — because the two tool rails end
// above the drawer and must move with it as it is dragged. `drawerExpanded` is
// derived from the same number so the map can react (e.g. hide the legend).
// The drawer widget only draws what it is given: the drag's writes and the
// chevron's toggle come back here as the store's own setters.
import { computed } from "vue";

import BottomDrawer from "@/components/panel/BottomDrawer.vue";
import { formatBigNumber } from "@/format";
import { useStoreMapReview } from "@/store/map-review";
import type {
	MapReviewColumnFilter,
	MapReviewSort,
	MapReviewTable,
} from "@/type/api";

import DataTable from "./DataTable.vue";
import TablePager from "./TablePager.vue";
import PopulationZoneRows from "./population/PopulationZoneRows.vue";
import { MAP_REVIEW_TABLE_LABELS } from "./columns";

const store = useStoreMapReview();

// The chosen table's filters, in the shape the table components take.
const activeFilters = computed<MapReviewColumnFilter[]>(() =>
	(store.spec.filters ?? []).filter((f) => f.table === store.activeTable),
);

function onSort(sort: MapReviewSort | null) {
	store.setSort(sort);
}

function onFilter(filter: MapReviewColumnFilter | null, column: string) {
	store.setFilter(filter, column, store.activeTable);
}

// A zone row's Records cell narrows the Feature ledger to that zone: the target
// table becomes the active one and the zone id becomes the spec's zone list. No
// filter clause is written — a zone's membership is geometric and the server
// resolves it from the spec, exactly as it resolves a drawn Area.
function onZoneLink(link: { table: MapReviewTable; id: number }) {
	store.setActiveTable(link.table);
	store.setZones([link.id]);
}
</script>

<template>
	<!-- A closed ledger renders nothing at all: the map keeps the whole box. -->
	<BottomDrawer
		v-if="store.drawerOpen"
		id="map-review-ledger"
		class="table-drawer"
		:fraction="store.drawerFraction"
		:expanded="store.drawerExpanded"
		@update:fraction="store.setDrawerFraction"
		@update:expanded="store.setDrawerExpanded"
	>
		<!-- The bar carries this page's table switcher — each tab's count the
		     live, spec-resolved total — and the close control; the drag target
		     and the expand/shrink preset are the widget's. The full-screen
		     table overlay is gone — expanding this drawer to the top is the
		     same gesture and keeps one surface to read. -->
		<template #grab>
			<div class="table-drawer-tabs" role="tablist" aria-label="Tables">
				<button
					v-for="t in store.ledgerTables"
					:key="t"
					type="button"
					role="tab"
					class="table-drawer-tab"
					:class="{ active: t === store.activeTable }"
					:aria-selected="t === store.activeTable"
					@pointerdown.stop
					@click="store.setActiveTable(t)"
				>
					<span>{{ MAP_REVIEW_TABLE_LABELS[t] }}</span>
					<span class="table-drawer-tab-count">
						{{ formatBigNumber(store.counts[t]) }}
					</span>
				</button>
			</div>
		</template>
		<template #grab-end>
			<button
				type="button"
				class="bottom-drawer-btn"
				aria-label="Close table drawer"
				title="Close table drawer"
				@pointerdown.stop
				@click="store.closeDrawer()"
			>
				<i class="bi bi-x-lg"></i>
			</button>
		</template>

		<!-- The population view's nine derived classes sit above the ledger they
		     narrow, outside the table's own sort/page region: only the class
		     toggle writes the spec, and it writes a temporary zone, never
		     `spec.zones`. -->
		<PopulationZoneRows
			v-if="store.activeTable === 'zone'"
			:view="store.population"
			:selected-class="store.populationClass"
			@select-class="store.togglePopulationClass"
		/>

		<div class="table-drawer-body">
			<DataTable
				:rows="store.rows"
				:table="store.activeTable"
				:total="store.total"
				:loading="store.loading"
				:selection="store.selection"
				:filters="activeFilters"
				:sort="store.sort"
				:filters-open="store.filtersOpen"
				:summary="store.summary"
				dense
				@select="(id: number) => store.select(store.activeTable, id)"
				@sort="onSort"
				@filter="onFilter"
				@follow="store.followRelation"
				@zone-link="onZoneLink"
			/>
		</div>

		<TablePager
			:page="store.page"
			:limit="store.limit"
			:total="store.total"
			:loading="store.loading"
			@page="store.setPage"
		/>
	</BottomDrawer>
</template>

<style scoped>
/* Placement only: the drawer widget owns its bar and the flex column under it,
   and this class is what tells it where Map Review's ledger sits. */
.table-drawer {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 20;
	background: #fff;
	border-top: 1px solid #ced4da;
	box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.12);
	min-height: 140px;
}

/* The table switcher, on the bar rather than in its own row: each tab carries
   the live count the grid's strip used to, and the bar's remaining width is the
   drag target. */
.table-drawer-tabs {
	display: flex;
	gap: 2px;
	min-width: 0;
	overflow: hidden;
}

.table-drawer-tab {
	display: flex;
	align-items: baseline;
	gap: 5px;
	padding: 2px 8px;
	border: 1px solid transparent;
	border-radius: 3px;
	background: transparent;
	color: #495057;
	font-size: 0.75rem;
	line-height: 1.2;
	white-space: nowrap;
}

.table-drawer-tab:hover {
	background: #fff;
}

.table-drawer-tab.active {
	background: #e7f1ff;
	border-color: #a5c9fe;
	color: #0a58ca;
	font-weight: 600;
}

.table-drawer-tab-count {
	color: #6c757d;
	font-variant-numeric: tabular-nums;
}

.table-drawer-tab.active .table-drawer-tab-count {
	color: #0a58ca;
}

.table-drawer-body {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
}
</style>
